import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import communicationLogService from '../../../api/services/communicationLogService';
import candidateService from '../../../api/services/candidateService';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import Alert from '../../../components/common/Alert/Alert';
import {
  ArrowLeft,
  Paperclip,
  FileText,
  Save
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';

const EditCommunication = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams();
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();
  const [formData, setFormData] = useState({
    subject: '',
    communicationType: 'email',
    priority: 'medium',
    recipient: '',
    cc: '',
    bcc: '',
    message: '',
    attachments: [],
    candidateId: '',
    offerId: '',
    followUpRequired: false,
    followUpDate: '',
    notes: '',
    tags: [],
    status: 'draft'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const nowIsoLocal = new Date().toISOString().slice(0, 16);

  // Load candidates
  const loadCandidates = async () => {
    try {
      const result = await candidateService.getCandidates({ limit: 100 });
      if (result && (result.responseCode === 200 || result.responseCode === 201) && result.data && result.data.candidates) {
        setCandidates(result.data.candidates);
      }
    } catch (error) {
    }
  };

  const communicationTypes = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'sms', label: 'SMS' },
    { value: 'video_call', label: 'Video Call' }
  ];

  const priorityLevels = [
    { value: 'low', label: 'Low', color: 'text-gray-600' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
    { value: 'high', label: 'High', color: 'text-orange-600' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-600' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'read', label: 'Read' },
    { value: 'replied', label: 'Replied' }
  ];

  // Load communication data
  const loadCommunication = async () => {
    try {
      setIsLoading(true);
      
      const result = await communicationLogService.getCommunicationLogById(id);
      
      if (result && (result.responseCode === 200 || result.responseCode === 201)) {
        const commData = result.data;
        
        setFormData({
          subject: commData.subject || '',
          communicationType: commData.communicationType || 'email',
          priority: commData.priority || 'medium',
          recipient: commData.recipient || '',
          cc: commData.cc || '',
          bcc: commData.bcc || '',
          message: commData.message || '',
          attachments: commData.attachments || [],
          candidateId: commData.candidateId || '',
          offerId: commData.offerId || '',
          followUpRequired: commData.followUpRequired || false,
          followUpDate: commData.followUpDate ? new Date(commData.followUpDate).toISOString().slice(0, 16) : '',
          notes: commData.notes || '',
          tags: commData.tags || [],
          status: commData.status || 'draft'
        });
        
      } else {
        showError('Failed to load communication details');
      }
    } catch (error) {
      showError('Error loading communication details');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadCandidates();
      loadCommunication();
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Live caps and sanitization
    if (name === 'subject') {
      // Only allow alphanumeric, spaces, and commas
      value = value.replace(/[^a-zA-Z0-9\s,]/g, '').substring(0, 150);
    } else if (name === 'message' || name === 'notes') {
      // Only allow alphanumeric, spaces, and commas
      value = value.replace(/[^a-zA-Z0-9\s,]/g, '');
      const MAX = name === 'message' ? 5000 : 1000;
      value = value.substring(0, MAX);
    } else if (name === 'recipient' || name === 'cc' || name === 'bcc') {
      const MAX = 254; if (value.length > MAX) value = value.slice(0, MAX);
      value = value.replace(/\s+/g, '');
    }

    if (name === 'followUpRequired') {
      // Coerce to boolean from select string
      value = value === 'true' || value === true;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateField = (name, value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let msg = '';
    switch (name) {
      case 'subject':
        if (!value || value.trim().length < 3) msg = 'Subject is required (min 3 characters).';
        else if (!/^[a-zA-Z0-9\s,]+$/.test(value.trim())) msg = 'Subject can only contain alphanumeric characters, spaces, and commas.';
        else if (value.length > 150) msg = 'Subject must be ≤ 150 characters.';
        break;
      case 'recipient':
        if (!value) msg = 'Recipient is required.';
        else if (!emailRegex.test(value)) msg = 'Enter a valid email address.';
        break;
      case 'cc':
      case 'bcc':
        if (value && !emailRegex.test(value)) msg = 'Enter a valid email address.';
        break;
      case 'message':
        if (!value || value.trim().length < 10) msg = 'Message must be at least 10 characters.';
        else if (!/^[a-zA-Z0-9\s,]+$/.test(value.trim())) msg = 'Message can only contain alphanumeric characters, spaces, and commas.';
        else if (value.length > 5000) msg = 'Message must be ≤ 5000 characters.';
        break;
      case 'notes':
        if (value && !/^[a-zA-Z0-9\s,]+$/.test(value.trim())) msg = 'Notes can only contain alphanumeric characters, spaces, and commas.';
        else if (value && value.length > 1000) msg = 'Notes must be ≤ 1000 characters.';
        break;
      case 'followUpDate': {
        const required = formData.followUpRequired === true || formData.followUpRequired === 'true';
        if (required && !value) {
          msg = 'Follow-up date is required when follow-up is set to Yes.';
          break;
        }
        if (value) {
          const selected = new Date(value);
          const now = new Date();
          if (selected.getTime() < now.getTime() - 60000) { // allow 1 min skew
            msg = 'Follow-up date/time cannot be in the past.';
          }
        }
        break;
      }
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [name]: msg }));
    return !msg;
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
    
    // Reset file input to allow selecting the same file again
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
    
    // Reset file input to allow reattaching the same file
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const validateForm = () => {
    const fields = ['subject','recipient','cc','bcc','message','notes','followUpDate'];
    let ok = true;
    fields.forEach(f => { if (!validateField(f, formData[f])) ok = false; });
    return ok;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Please fix the validation errors before submitting. All required fields must be filled correctly.');
      return;
    }
    
    // Check if all required fields are filled
    const requiredFields = ['subject', 'recipient', 'message'];
    const missingFields = requiredFields.filter(field => {
      return !formData[field] || formData[field].toString().trim() === '';
    });
    
    if (missingFields.length > 0) {
      showError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    
    try {
      
      const result = await communicationLogService.updateCommunicationLog(id, formData);
      
      if (result && (result.responseCode === 200 || result.responseCode === 201)) {
        showSuccess('Communication updated successfully!');
        setTimeout(() => {
          navigate('/offer_communication_logs');
        }, 2000);
      } else {
        showError('Failed to update communication');
      }
    } catch (error) {
      showError('Error updating communication');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/offer_communication_logs');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleCancel}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Communication</h1>
            <p className="text-gray-600 mt-1">Edit offer-related communication</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<Save className="h-4 w-4" />}
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Communication Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <FileText className="h-5 w-5 text-blue-600" />
          <span className="text-sm font-medium text-blue-800">
            Communication ID: {id} | Created: {new Date().toLocaleString()}
          </span>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                onKeyPress={(e) => {
                  // Only allow alphanumeric, spaces, and commas
                  if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onBlur={(e)=>validateField('subject', e.target.value)}
                maxLength={150}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.subject ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter communication subject"
                required
              />
              <div className="flex justify-between items-center mt-1">
                {errors.subject && (
                  <p className="text-sm text-red-600">{errors.subject}</p>
                )}
                <p className="text-xs text-gray-500 ml-auto">
                  {formData.subject?.length || 0}/150 characters
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Communication Type
              </label>
              <select
                name="communicationType"
                value={formData.communicationType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {communicationTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Recipient Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To *
              </label>
              <input
                type="text"
                name="recipient"
                value={formData.recipient}
                onChange={handleInputChange}
                onBlur={(e)=>validateField('recipient', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.recipient ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter recipient email or phone"
              />
              {errors.recipient && (
                <p className="mt-1 text-sm text-red-600">{errors.recipient}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {priorityLevels.map(priority => (
                  <option key={priority.value} value={priority.value}>
                    {priority.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* CC and BCC */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CC
              </label>
              <input
                type="text"
                name="cc"
                value={formData.cc}
                onChange={handleInputChange}
                onBlur={(e)=>validateField('cc', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter CC recipients"
              />
              {errors.cc && (
                <p className="mt-1 text-sm text-red-600">{errors.cc}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                BCC
              </label>
              <input
                type="text"
                name="bcc"
                value={formData.bcc}
                onChange={handleInputChange}
                onBlur={(e)=>validateField('bcc', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter BCC recipients"
              />
              {errors.bcc && (
                <p className="mt-1 text-sm text-red-600">{errors.bcc}</p>
              )}
            </div>
          </div>

          {/* Related Records */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Related Candidate
              </label>
              <select
                name="candidateId"
                value={formData.candidateId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a candidate</option>
                {candidates.map(candidate => (
                  <option key={candidate._id} value={candidate._id}>
                    {candidate.firstName} {candidate.lastName} - {candidate.jobTitle || 'N/A'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Related Offer
              </label>
              <select
                name="offerId"
                value={formData.offerId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select an offer</option>
                <option value={formData.offerId}>{formData.offerId}</option>
              </select>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message *
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              onKeyPress={(e) => {
                // Only allow alphanumeric, spaces, and commas
                if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onBlur={(e)=>validateField('message', e.target.value)}
              maxLength={5000}
              rows={8}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.message ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter your message here..."
              required
            />
            <div className="flex justify-between items-center mt-1">
              {errors.message && (
                <p className="text-sm text-red-600">{errors.message}</p>
              )}
              <p className="text-xs text-gray-500 ml-auto">
                {formData.message?.length || 0}/5000 characters
              </p>
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Attachments
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <div className="text-center">
                <Paperclip className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">
                  Drag and drop files here, or click to select
                </p>
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                >
                  <Paperclip className="h-4 w-4 mr-2" />
                  Add Files
                </label>
              </div>
              
              {formData.attachments.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Attached Files:</h4>
                  <div className="space-y-2">
                    {formData.attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm text-gray-700">
                          {file.name} {file.size && `(${file.size})`}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Follow-up and Status */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Follow-up Required
              </label>
              <select
                name="followUpRequired"
                value={formData.followUpRequired}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={false}>No</option>
                <option value={true}>Yes</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Follow-up Date
              </label>
              <input
                type="datetime-local"
                name="followUpDate"
                value={formData.followUpDate}
                onChange={handleInputChange}
                onBlur={(e)=>validateField('followUpDate', e.target.value)}
                min={nowIsoLocal}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.followUpDate && (
                <p className="mt-1 text-sm text-red-600">{errors.followUpDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              onKeyPress={(e) => {
                // Only allow alphanumeric, spaces, and commas
                if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onBlur={(e)=>validateField('notes', e.target.value)}
              maxLength={1000}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter any additional notes..."
            />
            <div className="flex justify-between items-center mt-1">
              {errors.notes && (
                <p className="text-sm text-red-600">{errors.notes}</p>
              )}
              <p className="text-xs text-gray-500 ml-auto">
                {formData.notes?.length || 0}/1000 characters
              </p>
            </div>
          </div>
        </form>
      </div>
      
      {/* Alert Component */}
      <Alert
        isOpen={alertState.show}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        showCancel={alertState.showCancel}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm || hideAlert}
        onCancel={hideAlert}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />
    </div>
  );
};

export default EditCommunication;
